import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import { fetchAssignmentListAPI } from "../worksheetAPI";
import { refinePages } from "../../../utils/paginationUtils";
import { formatDateString } from "../../../utils/textUtils";
import secret from "../../../secret.json";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";

import Pagination from "../../../components/Pagination";
import CustomCheckbox from "../../../components/CustomCheckbox";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import DeleteWorksheetModal from "./common/DeleteWorksheetModal";

const AssignedRetryWorksheetTable = () => {
  const { user } = useSelector((state) => state.auth);
  
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeSelect, setTypeSelect] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [selectedWorksheets, setSelectedWorksheets] = useState([]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages: totalPages, current_page: currentPage }),
    [currentPage, totalPages]
  );

  const fetchData = useCallback(async () => {
    try {
      const typeParam = typeSelect === "all" ? null : typeSelect;
      // Use the same API as regular assignments but we'll filter for retry assignments
      const response = await fetchAssignmentListAPI(
        currentPage,
        10,
        typeParam,
        null, // isFinished
        null, // levelId
        keyword,
        null, // studentId
        null  // studentGrade
      );
      
      // Filter for assignments that have retry info or are retry assignments
      const retryResults = response.results.filter(assignment => 
        assignment.is_retry
      );
      
      setResults(retryResults);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch retry assignments:", error);
    }
  }, [currentPage, typeSelect, keyword]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleCheckboxChange = (checked, worksheet) => {
    if (checked) {
      setSelectedWorksheets((prev) => [...prev, worksheet]);
    } else {
      setSelectedWorksheets((prev) =>
        prev.filter((prevWorksheet) => prevWorksheet.id !== worksheet.id)
      );
    }
  };

  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedWorksheets(results);
    } else {
      setSelectedWorksheets([]);
    }
  };

  const isHeaderChecked =
    results.length > 0 &&
    results.every((currentWorksheet) =>
      selectedWorksheets.some(
        (selectedWorksheet) => selectedWorksheet.id === currentWorksheet.id
      )
    );

  const handlePreviewClick = (assignment) => {
    const url = `${secret.STUDENT_BASE_URL}/preview?id=${assignment.id}&session=${user.session}`;
    window.open(url, "_blank");
  };

  return (
    <div
      style={{
        padding: "10px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h3 style={{ marginRight: "auto" }}>복습 문제지 목록</h3>
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div
          style={{
            width: "30%",
            marginLeft: "10px",
          }}
        >
          <WorksheetSearchBar
            placeholder="복습 문제지명 검색"
            handleFilter={setKeyword}
          />
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFF",
          paddingBottom: "5px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "10px",
          }}
        >
          <colgroup>
            <col style={{ width: "80px" }} />
            <col style={{ width: "110px" }} />
            <col />
            <col style={{ width: "120px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "180px" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1px solid #EBEBEB",
                fontSize: "16px",
                color: "#737373",
              }}
            >
              <td style={{ textAlign: "center" }}>
                <CustomCheckbox
                  checked={isHeaderChecked}
                  onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
                />
              </td>
              <td style={{ padding: "10px" }}>문항 번호</td>
              <td style={{ padding: "10px" }}>복습 문제지명</td>
              <td style={{ padding: "10px", textAlign: "center" }}>학생명</td>
              <td style={{ padding: "10px", textAlign: "center" }}>생성일</td>
              <td style={{ padding: "10px", textAlign: "center" }}>마감일</td>
              <td style={{ padding: "10px", textAlign: "center" }}>미리보기</td>
              <td style={{ padding: "10px", textAlign: "center" }}>제출 시간</td>
            </tr>
            {results &&
              results.map((item) => {
                const isChecked = selectedWorksheets.includes(item);
                return (
                  <tr
                    key={item.id}
                    style={{
                      fontSize: "18px",
                    }}
                  >
                    <td
                      style={{
                        textAlign: "center",
                      }}
                    >
                      <CustomCheckbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, item)
                        }
                      />
                    </td>
                    <td style={{ padding: "10px" }}>{item.id}</td>
                    <td
                      style={{
                        padding: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {item.is_retry && (
                        <span style={{ 
                          backgroundColor: "#e8f5e8", 
                          color: "#2e7d32", 
                          padding: "4px 8px", 
                          borderRadius: "4px", 
                          fontSize: "12px",
                          marginLeft: "8px",
                          marginRight: "8px",
                          fontWeight: "bold"
                        }}>
                          복습 {item.retry_info?.retry_count || ''}회차
                        </span>
                      )}
                      {item.title}
                      <WorksheetTypeBox type={item.type} />
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {item.student?.name || ""}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {formatDateString(item.created_at)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {formatDateString(item.closed_at)}
                    </td>
                    <td style={{ position: "relative" }}>
                      <button
                        onClick={() => handlePreviewClick(item)}
                        style={{
                          backgroundColor: "#F6F8FA",
                          border: "1px solid #EBEBEB",
                          borderRadius: "5px",
                          padding: "5px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                        title="문서 열기"
                      >
                        <DescriptionIcon
                          style={{
                            fill: "#000000",
                            fontSize: "24px",
                            stroke: "#EBEBEB",
                          }}
                        />
                      </button>
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        fontSize: "16px",
                        textAlign: "center",
                      }}
                    >
                      {item.finished_at ? formatDateString(item.finished_at) : "미제출"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <Pagination
          visiblePages={visiblePages}
          current_page={currentPage}
          handlePageClick={handlePageClick}
        />
      </div>

      {selectedWorksheets.length > 0 && (
        <DeleteWorksheetModal
          worksheets={selectedWorksheets}
          setSelectedWorksheets={setSelectedWorksheets}
          type={"retry"}
        />
      )}
    </div>
  );
};

export default AssignedRetryWorksheetTable;
